import i18n from 'i18n';
import { validateValidWords } from 'utils/ValidateUtils';
import { formatBytes } from 'utils/StringUtil';

/**
 * 渠道品牌設定的前端即時檢查。規則與後端相同、由後端判定為準；這裡只是讓使用者
 * 在存檔前就看到錯誤。全部欄位都是選填，trim 後為空一律視為沒填。
 */

export const BRAND_NAME_MAX_LENGTH = 50;
export const BRAND_URL_MAX_LENGTH = 500;
export const BRAND_ICON_VALID_TYPES = ['image/svg+xml', 'image/png'];
// 200 KB，與後端的上限同一個位元組數
export const BRAND_ICON_MAX_BYTES = 204800;

// 控制字元、行／段落分隔字元，以及零寬字元與雙向覆寫這類看不見的格式字元
const INVISIBLE_CHAR_RE = /[\p{Cc}\p{Zl}\p{Zp}\p{Cf}]/u;
const ANGLE_BRACKET_RE = /[<>]/;
const URL_OR_EMAIL_RE = /https?:\/\/|www\.|@/i;

/**
 * 三個語系的品牌名稱用同一套規則，不限定各語系能用哪些文字。
 */
export function validateBrandName(
  value: string | null | undefined
): string | undefined {
  const name = typeof value === 'string' ? value.trim() : '';
  if (!name) {
    return undefined;
  }
  // emoji 要比看不見的字元先檢查：組合 emoji 內含零寬連接字（U+200D），
  // 反過來會對一個 emoji 回「不可包含不可見字元」
  const emojiError = validateValidWords(name);
  if (emojiError) {
    return emojiError;
  }
  if (INVISIBLE_CHAR_RE.test(name) || ANGLE_BRACKET_RE.test(name)) {
    return i18n.t('channelGroupForm.labels.brandNameInvalidCharError');
  }
  // 以 code point 計算，一個中日文字或一個 emoji 都算一個字元
  if (Array.from(name).length > BRAND_NAME_MAX_LENGTH) {
    return i18n.t('formValidate.labels.maximumLengthError', {
      max: BRAND_NAME_MAX_LENGTH
    });
  }
  // 先做相容正規化，全形的「ｈｔｔｐ：／／」「＠」才比對得到
  if (URL_OR_EMAIL_RE.test(name.normalize('NFKC'))) {
    return i18n.t('channelGroupForm.labels.brandNameUrlError');
  }
  return undefined;
}

const IPV4_RE = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * 品牌連結。不能直接用 validateUrl：那支把空值當錯誤，而這個欄位是選填。
 * 只檢查格式，不實際連線。
 */
export function validateBrandUrl(
  value: string | null | undefined
): string | undefined {
  const url = typeof value === 'string' ? value.trim() : '';
  if (!url) {
    return undefined;
  }
  if (url.length > BRAND_URL_MAX_LENGTH) {
    return i18n.t('formValidate.labels.maximumLengthError', {
      max: BRAND_URL_MAX_LENGTH
    });
  }
  const invalid = i18n.t('channelGroupForm.labels.brandUrlError');
  // 只收 http / https，擋掉 javascript: 與 data: 這類會在點擊時執行內容的 scheme
  if (/\s/.test(url) || !/^https?:\/\//i.test(url)) {
    return invalid;
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return invalid;
  }
  // 看原字串而不是解析結果：「https://@example.com/」的 username 會被解析成空字串
  const authority = url.replace(/^https?:\/\//i, '').split(/[/?#]/)[0];
  const host = parsed.hostname.toLowerCase();
  if (
    authority.includes('@') ||
    parsed.username ||
    parsed.password ||
    !host.includes('.') ||
    host.startsWith('.') ||
    host.endsWith('.') ||
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    host.startsWith('[') ||
    IPV4_RE.test(host)
  ) {
    return invalid;
  }
  return undefined;
}

/**
 * 地圖圖示。沒換圖（只有既有 URL）就不重驗，那張圖上傳當下已經驗過，前端手上也沒有檔案。
 * 尺寸不檢查：SVG 不一定有固定尺寸，地圖上一律縮放成同一個大小顯示。
 */
export function validateBrandIcon(
  fileData: ImageFieldData | undefined
): string | undefined {
  const file = fileData?.file;
  if (!file) {
    return undefined;
  }
  if (!BRAND_ICON_VALID_TYPES.includes(file.type)) {
    return i18n.t('channelGroupForm.labels.brandIconTypeError');
  }
  if (file.size > BRAND_ICON_MAX_BYTES) {
    return i18n.t('creativeSetupFlow.labels.storageErrorHint', {
      storage1: formatBytes(file.size),
      storage2: '200KB'
    });
  }
  return undefined;
}
